import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function Analytics() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedDataType, setSelectedDataType] = useState('Both');
  const [presetDates, setPresetDates] = useState('Three Months');

  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate) {
        return;
      }

      try {
        setLoading(true);
        const start = new Date(`${startDate}T00:00:00`);
        const end = new Date(`${endDate}T23:59:59.999`);
        const withdrawalsCollection = query(
          collection(db, 'studentWithdrawals'),
          where('withdrawalDate', '>=', Timestamp.fromDate(start)),
          where('withdrawalDate', '<=', Timestamp.fromDate(end))
        );
        const newStudentsCollection = query(
          collection(db, 'newStudents'),
          where('enrollmentDate', '>=', Timestamp.fromDate(start)),
          where('enrollmentDate', '<=', Timestamp.fromDate(end))
        );
        const [withdrawalsSnapshot, newStudentsSnapshot] = await Promise.all([
          getDocs(withdrawalsCollection),
          getDocs(newStudentsCollection)
        ]);

        const withdrawals = withdrawalsSnapshot.docs.map(doc => ({
          ...doc.data(),
          date: doc.data().withdrawalDate.toDate(),
          type: 'withdrawal'
        }));

        const newStudents = newStudentsSnapshot.docs.map(doc => ({
          ...doc.data(),
          date: doc.data().enrollmentDate.toDate(),
          type: 'newStudent'
        }));

        setEntries([...withdrawals, ...newStudents]);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data: ", error);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  useEffect(() => {
    // Set initial date range to last three months
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth() - 3, 1);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    updateDatesFromPreset(presetDates);
  }, [presetDates]);

  const updateDatesFromPreset = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'Three Months':
        start = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'Six Months':
        start = new Date(today.getFullYear(), today.getMonth() - 6, 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'Twelve Months':
        start = new Date(today.getFullYear() - 1, today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'Last Year':
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case 'Year To Date':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date();
        break;
      case 'This Year':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;
      default:
        return; // Don't update for 'Custom'
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const getMonthlyEntryCounts = (entries) => {
    const monthlyCounts = {};
    entries.forEach(entry => {
      const monthYear = entry.date.toLocaleString('default', { month: 'short', year: '2-digit' });
      monthlyCounts[monthYear] = (monthlyCounts[monthYear] || 0) + 1;
    });
    return monthlyCounts;
  };

  const getClassroomCounts = (entries) => {
    const classroomCounts = {};
    entries.forEach(entry => {
      classroomCounts[entry.classroom] = (classroomCounts[entry.classroom] || 0) + 1;
    });
    return classroomCounts;
  };

  const withdrawals = entries.filter(entry => entry.type === 'withdrawal');
  const newStudents = entries.filter(entry => entry.type === 'newStudent');

  const getChartData = (dataType) => {
    const data = dataType === 'withdrawal' ? withdrawals : newStudents;
    const monthlyCounts = getMonthlyEntryCounts(data);
    const classroomCounts = getClassroomCounts(data);

    return {
      monthly: {
        labels: Object.keys(monthlyCounts),
        datasets: [{
          label: dataType === 'withdrawal' ? 'Withdrawals' : 'New Students',
          data: Object.values(monthlyCounts),
          backgroundColor: dataType === 'withdrawal' ? 'rgba(255, 99, 132, 0.6)' : 'rgba(75, 192, 192, 0.6)',
        }],
      },
      classroom: {
        labels: Object.keys(classroomCounts),
        datasets: [{
          label: dataType === 'withdrawal' ? 'Withdrawals' : 'New Students',
          data: Object.values(classroomCounts),
          backgroundColor: dataType === 'withdrawal' ? 'rgba(255, 99, 132, 0.6)' : 'rgba(75, 192, 192, 0.6)',
        }],
      },
      distribution: {
        labels: dataType === 'Both' ? ['Withdrawals', 'New Students'] : Object.keys(classroomCounts),
        datasets: [{
          data: dataType === 'Both' 
            ? [withdrawals.length, newStudents.length] 
            : Object.values(classroomCounts),
          backgroundColor: dataType === 'Both'
            ? ['rgba(255, 99, 132, 0.6)', 'rgba(75, 192, 192, 0.6)']
            : Object.keys(classroomCounts).map(() => dataType === 'withdrawal' ? 'rgba(255, 99, 132, 0.6)' : 'rgba(75, 192, 192, 0.6)'),
        }],
      },
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        font: {
          size: 16,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: selectedDataType === 'Both' 
          ? 'Distribution of Withdrawals and New Students'
          : `Distribution of ${selectedDataType} by Classroom`,
        font: {
          size: 16,
        },
      },
    },
  };

  if (loading) {
    return <div className="text-center mt-8">Loading...</div>;
  }

  return (
    <div className="p-4">
      <h1 className="text-3xl font-bold mb-6">Analytics</h1>

      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <label className="block mb-2">Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPresetDates('Custom');
            }}
            className="w-full p-2 border rounded"
          />
        </div>
        <div>
          <label className="block mb-2">End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPresetDates('Custom');
            }}
            className="w-full p-2 border rounded"
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block mb-2">Select From Preset Date:</label>
        <select
          value={presetDates}
          onChange={(e) => setPresetDates(e.target.value)}
          className="w-full p-2 border rounded"
        >
          <option value="Custom">Custom</option>
          <option value="Three Months">Three Months</option>
          <option value="Six Months">Six Months</option>
          <option value="Twelve Months">Twelve Months</option>
          <option value="Last Year">Last Year</option>
          <option value="Year To Date">Year To Date</option>
          <option value="This Year">This Year</option>
        </select>
      </div>

      <div className="mb-4">
        <label className="block mb-2">Select Data Type:</label>
        <div className="flex space-x-4">
          <label>
            <input
              type="radio"
              value="Withdrawals"
              checked={selectedDataType === 'Withdrawals'}
              onChange={(e) => setSelectedDataType(e.target.value)}
              className="mr-2"
            />
            Withdrawals
          </label>
          <label>
            <input
              type="radio"
              value="New Students"
              checked={selectedDataType === 'New Students'}
              onChange={(e) => setSelectedDataType(e.target.value)}
              className="mr-2"
            />
            New Students
          </label>
          <label>
            <input
              type="radio"
              value="Both"
              checked={selectedDataType === 'Both'}
              onChange={(e) => setSelectedDataType(e.target.value)}
              className="mr-2"
            />
            Both
          </label>
        </div>
      </div>

      <div className="mt-8">
        <Pie
          data={getChartData(selectedDataType === 'Both' ? 'Both' : (selectedDataType === 'Withdrawals' ? 'withdrawal' : 'newStudent')).distribution}
          options={pieChartOptions}
        />
      </div>

      {(selectedDataType === 'Withdrawals' || selectedDataType === 'Both') && (
        <>
          <div className="mt-8">
            <Bar
              data={getChartData('withdrawal').monthly}
              options={{...chartOptions, plugins: {...chartOptions.plugins, title: {display: true, text: 'Withdrawals by Month'}}}}
            />
          </div>
          <div className="mt-8">
            <Bar
              data={getChartData('withdrawal').classroom}
              options={{...chartOptions, plugins: {...chartOptions.plugins, title: {display: true, text: 'Withdrawals by Classroom'}}}}
            />
          </div>
        </>
      )}

      {(selectedDataType === 'New Students' || selectedDataType === 'Both') && (
        <>
          <div className="mt-8">
            <Bar
              data={getChartData('newStudent').monthly}
              options={{...chartOptions, plugins: {...chartOptions.plugins, title: {display: true, text: 'New Students by Month'}}}}
            />
          </div>
          <div className="mt-8">
            <Bar
              data={getChartData('newStudent').classroom}
              options={{...chartOptions, plugins: {...chartOptions.plugins, title: {display: true, text: 'New Students by Classroom'}}}}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default Analytics;
